import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  CalendarCheck, Clock, FileText, AlertTriangle, ClipboardCheck, Package,
  Wrench, ShoppingCart, Warehouse, Truck, BarChart3, Activity, Loader2,
  ArrowUpRight, DollarSign, ChevronRight, Calendar, User, CheckCircle2,
  AlertCircle, Settings2, ChevronDown, Zap, TrendingUp, Receipt, Briefcase,
  ArrowRight, ExternalLink, RefreshCw, Phone,
} from 'lucide-react';

import api, { phoneSystemAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getTenantTimezone, parseUTC } from '../lib/timezone';

/* ═══════════ Period selector options ═══════════ */
const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'all_time', label: 'All Time' },
];

/* ═══════════ Metric definitions ═══════════ */
const METRIC_DEFS = {
  jobs_today: { label: 'Jobs Today', icon: CalendarCheck, color: 'blue', to: '/dispatch', category: 'Business', entitlement: 'crm' },
  unscheduled_jobs: { label: 'Unscheduled Jobs', icon: Clock, color: 'amber', to: '/jobs', category: 'Business', entitlement: 'crm' },
  pending_estimates: { label: 'Pending Estimates', icon: FileText, color: 'purple', to: '/estimates', category: 'Business', entitlement: 'crm' },
  open_invoices: { label: 'Open Invoices', icon: Receipt, color: 'blue', to: '/invoices', category: 'Business', entitlement: 'crm' },
  overdue_invoices: { label: 'Overdue Invoices', icon: AlertTriangle, colorFn: (v) => v > 0 ? 'red' : 'green', to: '/invoices', category: 'Business', entitlement: 'crm' },
  job_revenue_earned: { label: 'Revenue Earned', icon: DollarSign, color: 'green', format: 'currency', category: 'Business', entitlement: 'crm' },
  jobs_completed: { label: 'Jobs Completed', icon: CheckCircle2, color: 'green', category: 'Business', entitlement: 'crm' },
  avg_job_size: { label: 'Avg Job Size', icon: TrendingUp, color: 'cyan', format: 'currency', category: 'Business', entitlement: 'crm' },
  total_new_jobs: { label: 'New Jobs Booked', icon: Briefcase, color: 'blue', category: 'Business', entitlement: 'crm' },
  inventory_value: { label: 'Inventory Value', icon: DollarSign, color: 'green', to: '/catalogue', format: 'currency_inv', category: 'Operations' },
  low_stock_items: { label: 'Low Stock Items', icon: AlertTriangle, colorFn: (v) => v > 0 ? 'red' : 'green', to: '/replenishment', category: 'Operations' },
  total_skus: { label: 'Total SKUs', icon: Package, color: 'blue', to: '/catalogue', category: 'Operations' },
  vehicles: { label: 'Vehicles', icon: Truck, color: 'teal', to: '/vans', category: 'Operations' },
  pending_transfers: { label: 'Pending Transfers', icon: ArrowUpRight, color: 'amber', to: '/warehouse', category: 'Operations' },
  materials_awaiting_confirmation: { label: 'Materials Awaiting', icon: ClipboardCheck, color: 'orange', to: '/jobs', category: 'Operations', entitlement: 'crm' },
};

/* ═══════════ Reusable KPI Card ═══════════ */
const ACCENT_COLORS = {
  blue: 'bg-blue-500', green: 'bg-emerald-500', amber: 'bg-amber-500',
  red: 'bg-red-500', purple: 'bg-purple-500', cyan: 'bg-cyan-500',
  slate: 'bg-slate-500', rose: 'bg-rose-500', teal: 'bg-teal-500',
  orange: 'bg-orange-500',
};

const KPICard = ({ label, value, icon: Icon, color = 'blue', onClick, testId }) => (
  <Card
    className={`relative overflow-hidden transition-all duration-200 ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}`}
    onClick={onClick} data-testid={testId}
  >
    <div className={`absolute left-0 top-0 bottom-0 w-1 ${ACCENT_COLORS[color] || ACCENT_COLORS.blue}`} />
    <CardContent className="p-4 pl-5">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide truncate">{label}</p>
          <p className="text-2xl font-bold mt-1 tabular-nums">{value ?? '—'}</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/50 shrink-0">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
    </CardContent>
  </Card>
);

/* ═══════════ Distribution bar ═══════════ */
const DistributionBar = ({ warehousePercent, vehiclePercent }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span className="flex items-center gap-1"><Warehouse className="w-3 h-3" /> Warehouses {warehousePercent}%</span>
      <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Vehicles {vehiclePercent}%</span>
    </div>
    <div className="h-2 rounded-full bg-muted overflow-hidden flex">
      <div className="bg-blue-500 transition-all" style={{ width: `${warehousePercent}%` }} />
      <div className="bg-emerald-500 transition-all" style={{ width: `${vehiclePercent}%` }} />
    </div>
  </div>
);

/* ═══════════ Schedule row ═══════════ */
const ScheduleRow = ({ event }) => {
  const start = event.start_at ? parseUTC(event.start_at) : null;
  const tz = getTenantTimezone();
  const time = start ? new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(start) : '—';
  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-700', en_route: 'bg-amber-100 text-amber-700',
    on_site: 'bg-purple-100 text-purple-700', done: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-muted/50 transition-colors">
      <span className="text-xs font-mono text-muted-foreground w-14 shrink-0">{time}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{event.job_name || event.job_number}</p>
        {event.tech_name && <p className="text-xs text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" />{event.tech_name}</p>}
      </div>
      <Badge variant="secondary" className={`text-[10px] ${statusColors[event.status] || ''}`}>
        {event.status}
      </Badge>
    </div>
  );
};

/* ═══════════ Section heading ═══════════ */
const SectionHeading = ({ icon: Icon, children, action }) => (
  <div className="flex items-center justify-between">
    <h2 className="text-base font-semibold flex items-center gap-2 text-foreground">
      <Icon className="w-4 h-4 text-muted-foreground" />
      {children}
    </h2>
    {action}
  </div>
);

/* ═══════════ Time ago helper ═══════════ */
const formatTimeAgo = (date) => {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ═══════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════════════════════ */
export const Dashboard = () => {
  const navigate = useNavigate();
  const { hasEntitlement, hasAddon } = useAuth();
  const hasCrm = hasEntitlement('crm');
  const hasPhone = hasAddon('phone_system');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => localStorage.getItem('dashboard_period') || 'this_month');
  const [visibleMetrics, setVisibleMetrics] = useState([]);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [maint, setMaint] = useState(null);
  const [maintActivity, setMaintActivity] = useState(null);
  const [portRequests, setPortRequests] = useState([]);

  const fetchDashboard = useCallback(async (p) => {
    try {
      const res = await api.get(`/dashboard`, { params: { period: p } });
      setData(res.data);
      setVisibleMetrics(res.data.visible_metrics || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) return;
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(period); }, [period, fetchDashboard]);

  useEffect(() => {
    api.get('/dashboard/maintenance-overview')
      .then(r => setMaint(r.data))
      .catch(() => {});
    api.get('/dashboard/maintenance-activity')
      .then(r => { if (r.data) setMaintActivity(r.data.activity); })
      .catch(() => {});
    if (hasPhone) {
      phoneSystemAPI.portRequests()
        .then(r => setPortRequests(r.data || []))
        .catch(() => {});
    }
  }, [hasPhone]);

  const changePeriod = (p) => {
    setPeriod(p);
    localStorage.setItem('dashboard_period', p);
    setLoading(true);
  };

  const toggleMetric = async (metricId) => {
    const next = visibleMetrics.includes(metricId)
      ? visibleMetrics.filter(m => m !== metricId)
      : [...visibleMetrics, metricId];
    setVisibleMetrics(next);
    try {
      await api.put('/dashboard/preferences', { visible_metrics: next });
    } catch { /* silent */ }
  };

  /* ─── Derived ─── */
  const kpi = data?.kpi_metrics || {};
  const inv = data?.inventory_overview || {};
  const needs = data?.needs_attention || {};
  const needsCounts = needs.counts || {};
  const tr = data?.truck_readiness || {};
  const crm = data?.crm_snapshot || {};
  const sched = data?.scheduling_stats || {};
  const recentEvents = data?.recent_events || [];
  const todaysSchedule = crm.todays_schedule || [];
  const notReadyCount = tr.not_ready_count || 0;

  /* Build KPI metric values: merge kpi + inventory */
  const metricValues = {
    ...kpi,
    inventory_value: inv.inventory_value,
    low_stock_items: inv.low_stock_count,
    total_skus: inv.total_skus,
    vehicles: inv.vehicles_count,
  };

  const formatValue = (id, raw) => {
    const def = METRIC_DEFS[id];
    if (raw == null) return '—';
    if (def?.format === 'currency') return `$${Number(raw).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    if (def?.format === 'currency_inv') return `$${Number(raw).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    return raw;
  };

  const getColor = (id, raw) => {
    const def = METRIC_DEFS[id];
    if (def?.colorFn) return def.colorFn(raw);
    return def?.color || 'blue';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const totalNeedsAttention = needsCounts.low_stock + needsCounts.pending_approvals + needsCounts.pending_transfers + (hasCrm ? needsCounts.materials_awaiting_confirmation : 0);

  return (
    <Layout>
      <div className="space-y-8 pb-8" data-testid="dashboard">

        {/* ═══════════ TRUCK READINESS BANNER ═══════════ */}
        {notReadyCount > 0 && (
          <button
            onClick={() => navigate('/replenishment')}
            className="w-full flex items-center gap-3 px-5 py-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 hover:bg-amber-100 transition-colors text-left"
            data-testid="truck-readiness-banner"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <span className="text-sm font-semibold flex-1">
              {notReadyCount} truck{notReadyCount !== 1 ? 's' : ''} {notReadyCount !== 1 ? 'are' : 'is'} not ready for today's jobs
            </span>
            <span className="text-xs font-medium text-amber-700 flex items-center gap-1">
              View Fixes <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </button>
        )}

        {/* ═══════════ PERIOD SELECTOR + CUSTOMIZE ROW ═══════════ */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => changePeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  period === p.value
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-card text-muted-foreground border-border hover:bg-muted'
                }`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <Popover open={customizeOpen} onOpenChange={setCustomizeOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" data-testid="customize-metrics-btn">
                <Settings2 className="w-3.5 h-3.5" /> Customize Metrics
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3" data-testid="customize-metrics-popover">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Toggle Metrics</p>
              <div className="space-y-0.5 max-h-[320px] overflow-y-auto">
                {Object.entries(METRIC_DEFS).filter(([, def]) => !def.entitlement || hasEntitlement(def.entitlement)).map(([id, def]) => (
                  <label key={id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={visibleMetrics.includes(id)}
                      onChange={() => toggleMetric(id)}
                      className="rounded border-gray-300"
                      data-testid={`metric-toggle-${id}`}
                    />
                    <def.icon className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{def.label}</span>
                    <span className="ml-auto text-[10px] text-muted-foreground">{def.category}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* ═══════════ CONFIGURABLE KPI ROW ═══════════ */}
        {visibleMetrics.length > 0 && (
          <section data-testid="section-kpi-row">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {visibleMetrics.map(id => {
                const def = METRIC_DEFS[id];
                if (!def) return null;
                // Skip metrics that require an entitlement the tenant doesn't have
                if (def.entitlement && !hasEntitlement(def.entitlement)) return null;
                const raw = metricValues[id];
                return (
                  <KPICard
                    key={id}
                    label={def.label}
                    value={formatValue(id, raw)}
                    icon={def.icon}
                    color={getColor(id, raw)}
                    onClick={def.to ? () => navigate(def.to) : undefined}
                    testId={`kpi-${id}`}
                  />
                );
              })}
            </div>
          </section>
        )}

        {/* ═══════════ MAINTENANCE OVERVIEW ═══════════ */}
        {hasCrm && maint && maint.active_plans > 0 && (
          <section data-testid="section-maintenance-overview">
            <SectionHeading icon={RefreshCw}>Maintenance Overview</SectionHeading>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
              <KPICard
                label="Active Plans"
                value={maint.active_plans}
                icon={RefreshCw}
                color="purple"
                onClick={() => navigate('/maintenance-plans')}
                testId="maint-active-plans"
              />
              <KPICard
                label="Monthly Revenue"
                value={`$${maint.mrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={DollarSign}
                color="green"
                testId="maint-mrr"
              />
              <KPICard
                label="Upcoming Billings (7 days)"
                value={maint.upcoming_billings}
                icon={Receipt}
                color={maint.upcoming_billings > 0 ? 'amber' : 'slate'}
                testId="maint-upcoming-billings"
              />
              <KPICard
                label="Upcoming Services (7 days)"
                value={maint.upcoming_services}
                icon={Wrench}
                color={maint.upcoming_services > 0 ? 'blue' : 'slate'}
                onClick={() => navigate('/jobs')}
                testId="maint-upcoming-services"
              />
            </div>
            {maintActivity && maintActivity.length > 0 && (
              <Card className="mt-3" data-testid="maint-recent-activity">
                <CardHeader className="pb-2 px-4 pt-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Activity className="w-4 h-4" /> Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {maintActivity.map((item, i) => {
                      const EVENT_META = {
                        enrollment_created: { icon: User, color: 'text-emerald-500', desc: 'Enrolled' },
                        enrollment_canceled: { icon: AlertCircle, color: 'text-red-500', desc: 'Enrollment canceled' },
                        invoice_generated: { icon: Receipt, color: 'text-blue-500', desc: 'Invoice generated' },
                        job_generated: { icon: Wrench, color: 'text-violet-500', desc: 'Job created' },
                      };
                      const meta = EVENT_META[item.type] || { icon: Activity, color: 'text-stone-400', desc: item.type };
                      const IconComp = meta.icon;
                      const ts = item.timestamp ? new Date(item.timestamp) : null;
                      const timeAgo = ts ? formatTimeAgo(ts) : '';
                      const desc = item.type === 'enrollment_created' ? (item.label || meta.desc) : meta.desc;

                      const handleClick = () => {
                        if (item.link_type === 'invoice') navigate(`/invoices/${item.link_id}`);
                        else if (item.link_type === 'job') navigate(`/jobs/${item.link_id}`);
                        else if (item.link_type === 'customer') navigate(`/customers/${item.link_id}`);
                      };

                      return (
                        <button
                          key={`${item.type}-${i}`}
                          onClick={handleClick}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors text-left"
                          data-testid={`maint-activity-${i}`}
                        >
                          <IconComp className={`w-4 h-4 shrink-0 ${meta.color}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {item.customer_name}
                              {item.plan_name && <span className="text-muted-foreground font-normal"> — {item.plan_name}</span>}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{desc} &bull; {timeAgo}</p>
                          </div>
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </section>
        )}

        {/* ═══════════ TRUCK READINESS WIDGET ═══════════ */}
        {tr.total_checked > 0 && (
          <section data-testid="section-truck-readiness">
            <SectionHeading icon={Truck}>
              Truck Readiness
              {notReadyCount > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">{notReadyCount} Need Attention</Badge>
              )}
            </SectionHeading>
            <Card className="mt-3">
              <CardContent className="p-0 divide-y">
                {tr.trucks?.map((truck) => (
                  <div key={truck.van_id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                    <div className={`mt-0.5 p-1.5 rounded-lg ${truck.status === 'ready' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      <Truck className={`w-4 h-4 ${truck.status === 'ready' ? 'text-emerald-600' : 'text-amber-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{truck.van_name}</p>
                        {truck.tech_name && <span className="text-xs text-muted-foreground">({truck.tech_name})</span>}
                        <Badge variant={truck.status === 'ready' ? 'secondary' : 'destructive'} className="text-[10px]">
                          {truck.status === 'ready' ? 'Ready' : `${truck.issues.length} Issue${truck.issues.length !== 1 ? 's' : ''}`}
                        </Badge>
                      </div>
                      {truck.issues.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {truck.issues.slice(0, 3).map((issue) => (
                            <p key={issue.item_id} className="text-xs text-muted-foreground">
                              <span className={issue.severity === 'missing' ? 'text-red-600 font-medium' : 'text-amber-600 font-medium'}>
                                {issue.severity === 'missing' ? 'Missing' : 'Low'}
                              </span>
                              {' '}{issue.item_name}
                              <span className="text-muted-foreground/60"> (need {issue.needed}, have {issue.on_hand})</span>
                            </p>
                          ))}
                          {truck.issues.length > 3 && (
                            <p className="text-xs text-muted-foreground/60">+{truck.issues.length - 3} more</p>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{truck.job_count} job{truck.job_count !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </CardContent>
              {notReadyCount > 0 && (
                <div className="px-4 py-2.5 border-t bg-muted/20">
                  <button
                    onClick={() => navigate('/replenishment')}
                    className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                    data-testid="truck-readiness-open-replenishment"
                  >
                    Open Replenishment <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              )}
            </Card>
          </section>
        )}

        {/* ═══════════ NEEDS ATTENTION WIDGET ═══════════ */}
        {totalNeedsAttention > 0 && (
          <section data-testid="section-needs-attention">
            <SectionHeading icon={AlertCircle}>Needs Attention</SectionHeading>
            <Card className="mt-3">
              <CardContent className="p-0 divide-y">
                {needsCounts.pending_transfers > 0 && (
                  <button onClick={() => navigate('/warehouse')} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left" data-testid="needs-pending-transfers">
                    <ArrowUpRight className="w-4 h-4 text-amber-500" />
                    <span className="flex-1 text-sm">Pending Warehouse Transfers</span>
                    <Badge variant="secondary">{needsCounts.pending_transfers}</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
                {needsCounts.low_stock > 0 && (
                  <button onClick={() => navigate('/replenishment')} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left" data-testid="needs-low-stock">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="flex-1 text-sm">Low Stock Items</span>
                    <Badge variant="secondary">{needsCounts.low_stock}</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
                {hasCrm && needsCounts.materials_awaiting_confirmation > 0 && (
                  <button onClick={() => navigate('/jobs')} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left" data-testid="needs-materials-awaiting">
                    <ClipboardCheck className="w-4 h-4 text-orange-500" />
                    <span className="flex-1 text-sm">Materials Awaiting Confirmation</span>
                    <Badge variant="secondary">{needsCounts.materials_awaiting_confirmation}</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
                {needsCounts.pending_approvals > 0 && (
                  <button onClick={() => navigate('/approvals')} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left" data-testid="needs-pending-approvals">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    <span className="flex-1 text-sm">Pending Approvals</span>
                    <Badge variant="secondary">{needsCounts.pending_approvals}</Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </CardContent>
            </Card>
          </section>
        )}

        {/* ═══════════ OPERATIONS STATUS (compact pills) ═══════════ */}
        <section data-testid="section-operations">
          <SectionHeading icon={ClipboardCheck}>Operations Status</SectionHeading>
          <div className="flex flex-wrap gap-3 mt-3">
            {[
              ...(hasCrm ? [{ label: 'Ready to Schedule', value: 0, icon: Calendar, to: '/dispatch', testId: 'ops-ready-schedule' }] : []),
              { label: 'Job Kits Ready', value: sched.ready_kits ?? 0, icon: Package, to: '/job-kits', testId: 'ops-kits-ready' },
              { label: 'Pending Approvals', value: needsCounts.pending_approvals ?? 0, icon: CheckCircle2, to: '/approvals', testId: 'ops-pending-approvals' },
              { label: 'Part Requests', value: sched.pending_parts ?? 0, icon: ShoppingCart, to: '/part-requests', testId: 'ops-part-requests' },
            ].map((s) => (
              <button
                key={s.testId}
                onClick={() => navigate(s.to)}
                className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full border bg-card hover:bg-muted/60 hover:shadow-sm transition-all cursor-pointer"
                data-testid={s.testId}
              >
                <s.icon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{s.label}</span>
                <span className="text-lg font-bold tabular-nums text-foreground">{s.value}</span>
              </button>
            ))}
          </div>
        </section>

        {/* ═══════════ PORT REQUESTS WIDGET ═══════════ */}
        {hasPhone && portRequests.length > 0 && (() => {
          const active = portRequests.filter(r => !['completed', 'cancelled', 'rejected'].includes(r.status));
          const attention = portRequests.filter(r => ['docs_needed', 'waiting_for_signature'].includes(r.status));
          if (active.length === 0) return null;
          const latestUpdate = portRequests.reduce((latest, r) => {
            const t = r.updated_at || r.created_at;
            return t && (!latest || new Date(t) > new Date(latest)) ? t : latest;
          }, null);
          return (
            <section data-testid="section-port-requests">
              <Card
                className={`cursor-pointer hover:shadow-md transition-all ${attention.length > 0 ? 'border-orange-300 bg-orange-50/30' : ''}`}
                onClick={() => navigate('/settings/phone-numbers?tab=porting')}
                data-testid="port-requests-widget"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`p-2 rounded-lg shrink-0 ${attention.length > 0 ? 'bg-orange-100' : 'bg-blue-100'}`}>
                        <Phone className={`w-4 h-4 ${attention.length > 0 ? 'text-orange-600' : 'text-blue-600'}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium">Number Porting</p>
                          {attention.length > 0 && (
                            <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]" data-testid="port-attention-badge">
                              {attention.length} need{attention.length > 1 ? '' : 's'} attention
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {active.length} active request{active.length !== 1 ? 's' : ''}
                          {latestUpdate && ` · Updated ${new Intl.DateTimeFormat('en-US', { timeZone: getTenantTimezone(), month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(parseUTC(latestUpdate))}`}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </section>
          );
        })()}

        {/* ═══════════ INVENTORY OVERVIEW ═══════════ */}
        {inv.visible && (
          <section data-testid="section-inventory">
            <SectionHeading icon={Package}>Inventory Overview</SectionHeading>
            <div className="space-y-4 mt-3">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard label="Inventory Value" value={`$${Number(inv.inventory_value || 0).toLocaleString()}`} icon={DollarSign} color="green" testId="metric-inv-value" onClick={() => navigate('/catalogue')} />
                <KPICard label="Low Stock" value={inv.low_stock_count ?? 0} icon={AlertTriangle} color={inv.low_stock_count > 0 ? 'red' : 'green'} onClick={() => navigate('/replenishment')} testId="metric-low-stock" />
                <KPICard label="Total SKUs" value={inv.total_skus ?? 0} icon={Package} color="blue" testId="metric-total-skus" onClick={() => navigate('/catalogue')} />
                <KPICard label="Vehicles" value={inv.vehicles_count ?? 0} icon={Truck} color="teal" testId="metric-vehicles" onClick={() => navigate('/vans')} />
              </div>
              {inv.distribution && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Stock Distribution</p>
                    <DistributionBar warehousePercent={inv.distribution.warehouse_percent ?? 0} vehiclePercent={inv.distribution.vehicle_percent ?? 0} />
                  </CardContent>
                </Card>
              )}
              <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/fleet-health')} data-testid="fleet-health-card">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Fleet Inventory Health</p>
                      <p className="text-xs text-muted-foreground">
                        {inv.tools_checked_out ?? 0} tools checked out · {inv.warehouses_count ?? 0} warehouse{(inv.warehouses_count ?? 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* ═══════════ ACTIVITY + SCHEDULE ═══════════ */}
        <section data-testid="section-activity">
          <SectionHeading icon={Activity}>Activity & Workflow</SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
            {/* Recent Inventory Activity */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Activity className="w-4 h-4" /> Recent Activity
                  </CardTitle>
                  <button
                    data-testid="view-all-activity-btn"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => navigate('/inventory/activity')}
                  >
                    View All <ArrowUpRight className="w-3 h-3" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {(!data?.recent_inventory_activity || data.recent_inventory_activity.length === 0) ? (
                  <div className="text-center py-8">
                    <Activity className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">No recent activity</p>
                    <button className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1" onClick={() => navigate('/inventory/activity')}>
                      View Activity Log <ArrowUpRight className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="divide-y max-h-[320px] overflow-y-auto">
                    {data.recent_inventory_activity.slice(0, 10).map((a, i) => {
                      const actionColors = {
                        transfer: 'text-blue-600',
                        receive: 'text-emerald-600',
                        adjust: 'text-amber-600',
                        assign: 'text-purple-600',
                        consume: 'text-red-600',
                        return: 'text-teal-600',
                      };
                      const actionLabel = (a.action_type || '').charAt(0).toUpperCase() + (a.action_type || '').slice(1);
                      return (
                        <div
                          key={a.id || i}
                          className="px-4 py-2.5 flex items-start gap-3 hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate('/inventory/activity')}
                          data-testid={`dashboard-activity-row-${i}`}
                        >
                          <div className="mt-0.5 p-1 rounded bg-muted">
                            <ArrowUpRight className={`w-3 h-3 ${actionColors[a.action_type] || 'text-muted-foreground'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">
                              <span className="font-medium">{a.item_name || 'Unknown'}</span>
                              <span className="text-muted-foreground"> · {actionLabel}</span>
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {a.from_location_name && a.to_location_name
                                ? `${a.from_location_name} → ${a.to_location_name}`
                                : a.to_location_name
                                  ? `→ ${a.to_location_name}`
                                  : a.from_location_name
                                    ? `${a.from_location_name} →`
                                    : ''}
                              {a.quantity !== undefined && ` · qty: ${a.quantity > 0 ? '+' : ''}${a.quantity}`}
                              {a.user_name && ` · ${a.user_name}`}
                            </p>
                          </div>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                            {a.created_at && new Intl.DateTimeFormat('en-US', { timeZone: getTenantTimezone(), hour: '2-digit', minute: '2-digit' }).format(parseUTC(a.created_at))}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Today's Schedule (CRM/Dispatch only) */}
            {hasCrm && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Today's Schedule
                </CardTitle>
                <CardDescription className="text-xs">
                  {todaysSchedule.length > 0
                    ? `${todaysSchedule.length} job${todaysSchedule.length !== 1 ? 's' : ''} scheduled`
                    : 'No jobs scheduled for today'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {todaysSchedule.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">No dispatches today</p>
                    <button className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1" onClick={() => navigate('/dispatch')}>
                      Open Dispatch <ArrowUpRight className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="divide-y max-h-[320px] overflow-y-auto">
                    {todaysSchedule.map((evt) => <ScheduleRow key={evt.id} event={evt} />)}
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </div>
        </section>

      </div>
    </Layout>
  );
};
